/**
 * PublicNoteRow
 *
 * Rich, read-only note row for the public artifact list. Mirrors the
 * authenticated `NoteCard` (color dot + title + author + date) but is fully
 * prop-driven so it works on the unauthenticated share/embed pages.
 *
 * @module components/share/public/PublicArtifactList/parts/PublicNoteRow
 */
import { memo, type CSSProperties } from 'react';
import { IconCalendar } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { UserAvatar } from '@/components/user/UserAvatar';
import { useLocalizedDate } from '@/hooks/useLocalizedDate';
import { ICON_SIZE_TAB } from '@/constants/ui';
import type { PublicNoteListItem } from '@/types/share';

import { PublicArtifactRowCard } from './PublicArtifactRowCard';
import styles from './PublicArtifactRows.module.css';

interface PublicNoteRowProps {
  note: PublicNoteListItem;
  onClick: () => void;
}

export const PublicNoteRow = memo(function PublicNoteRow({
  note,
  onClick,
}: PublicNoteRowProps) {
  const { t } = useTranslation();
  const { formatDateCompact } = useLocalizedDate();

  const dotStyle = { '--note-dot-color': note.color } as CSSProperties;

  return (
    <PublicArtifactRowCard
      ariaLabel={`${t('notes.openDetails')}: ${note.title}`}
      onClick={onClick}
    >
      <div className={styles.titleRow}>
        <span className={styles.colorDot} style={dotStyle} aria-hidden="true" />
        <span className={styles.title}>{note.title}</span>
      </div>

      <div className={styles.metaRow}>
        {note.creatorName !== null && (
          <div className={styles.metaGroup}>
            <UserAvatar
              displayName={note.creatorName}
              avatarUrl={note.creatorAvatarUrl ?? undefined}
              size="xs"
            />
            <span className={styles.metaText}>{note.creatorName}</span>
          </div>
        )}
        <div className={styles.metaGroup}>
          <IconCalendar size={ICON_SIZE_TAB} className={styles.metaIcon} />
          <span className={styles.metaText}>
            {formatDateCompact(note.updatedAt ?? note.createdAt)}
          </span>
        </div>
      </div>
    </PublicArtifactRowCard>
  );
});
